// Enterprise-level SEO utilities for technical optimization
import { supabase } from '@/integrations/supabase/client';

export interface PageSEOData {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  lastModified: string;
  priority: number;
  changeFreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
}

export class TechnicalSEOManager {
  private baseUrl = 'https://carolinatrophyroofs.com';

  // Generate optimized title tags with keyword targeting
  generateTitle(template: string, variables: Record<string, string>): string {
    let title = template;
    
    // Replace variables
    Object.entries(variables).forEach(([key, value]) => {
      title = title.replace(new RegExp(`{${key}}`, 'g'), value);
    });

    // Ensure title is under 60 characters for optimal display
    if (title.length > 60) {
      const parts = title.split(' | ');
      if (parts.length > 1) {
        // Try removing brand suffix if too long
        const withoutBrand = parts[0];
        if (withoutBrand.length <= 60) {
          title = withoutBrand;
        } else {
          // Truncate and add ellipsis
          title = withoutBrand.substring(0, 57) + '...';
        }
      }
    }

    return title;
  }

  // Generate meta descriptions with optimal length and CTA
  generateDescription(template: string, variables: Record<string, string>): string {
    let description = template;
    
    // Replace variables
    Object.entries(variables).forEach(([key, value]) => {
      description = description.replace(new RegExp(`{${key}}`, 'g'), value);
    });

    // Ensure description is between 150-160 characters
    if (description.length > 160) {
      description = description.substring(0, 157) + '...';
    }

    return description;
  }

  // Generate location-specific SEO data
  async generateLocationSEO(cityName: string, stateName: string): Promise<PageSEOData> {
    const titleTemplate = "Best Roofer Near Me in {city}, {state} | Carolina Trophy Roofs";
    const descriptionTemplate = "Top-rated roofing contractor in {city}, {state}. Free estimates, A+ BBB rating, 15+ years experience. Call (252) 302-2003 for expert roof repair & replacement.";
    
    const variables = {
      city: cityName,
      state: stateName
    };

    return {
      title: this.generateTitle(titleTemplate, variables),
      description: this.generateDescription(descriptionTemplate, variables),
      keywords: [
        `best roofer near me ${cityName}`,
        `roofing contractor ${cityName}`,
        `roof repair ${cityName}`,
        `roof replacement ${cityName} ${stateName}`,
        `storm damage ${cityName}`,
        `emergency roofing ${cityName}`,
        `residential roofing ${cityName}`,
        `commercial roofing ${cityName}`
      ],
      canonicalUrl: `${this.baseUrl}/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}`,
      lastModified: new Date().toISOString(),
      priority: 0.8,
      changeFreq: 'monthly'
    };
  }

  // Generate blog post SEO data
  async generateBlogSEO(post: any): Promise<PageSEOData> {
    const titleTemplate = post.seo_title || post.title;
    const descriptionTemplate = post.seo_description || post.excerpt || `Expert roofing insights and tips from Carolina Trophy Roofs. ${post.title}`;
    
    return {
      title: titleTemplate.length > 60 ? titleTemplate.substring(0, 57) + '...' : titleTemplate,
      description: descriptionTemplate.length > 160 ? descriptionTemplate.substring(0, 157) + '...' : descriptionTemplate,
      keywords: post.seo_keywords || post.tags || ['roofing tips', 'roof maintenance', 'roofing contractor'],
      canonicalUrl: `${this.baseUrl}/blog/${post.slug}`,
      lastModified: post.updated_at || post.created_at,
      priority: 0.8,
      changeFreq: 'weekly'
    };
  }

  // Detect and fix common SEO issues
  async auditPage(url: string): Promise<{
    issues: string[];
    recommendations: string[];
    score: number;
  }> {
    const issues: string[] = [];
    const recommendations: string[] = [];
    let score = 100;

    try {
      // Check if page exists in sitemap
      const response = await fetch(`${this.baseUrl}/sitemap.xml`);
      const sitemapText = await response.text();
      
      if (!sitemapText.includes(url)) {
        issues.push('Page not found in XML sitemap');
        recommendations.push('Add page to sitemap for better discoverability');
        score -= 10;
      }

      // Check for proper URL structure
      if (url.includes('?') && !url.includes('utm_')) {
        issues.push('URL contains query parameters that may cause duplicate content');
        recommendations.push('Use clean URLs without unnecessary parameters');
        score -= 5;
      }

      // Check URL length
      if (url.length > 100) {
        issues.push('URL is too long (>100 characters)');
        recommendations.push('Shorten URL for better user experience and SEO');
        score -= 5;
      }

    } catch (error) {
      console.error('Error auditing page:', error);
    }

    return { issues, recommendations, score };
  }

  // Generate internal linking suggestions
  async generateInternalLinks(currentPage: string, content: string): Promise<Array<{
    anchor: string;
    url: string;
    relevance: number;
  }>> {
    const suggestions: Array<{ anchor: string; url: string; relevance: number }> = [];

    // Common roofing-related internal links
    const linkOpportunities = [
      { keyword: 'roof repair', url: '/services/repair', anchor: 'professional roof repair services' },
      { keyword: 'roof replacement', url: '/services/replacement', anchor: 'complete roof replacement' },
      { keyword: 'storm damage', url: '/services/storm-damage', anchor: 'storm damage repair' },
      { keyword: 'free estimate', url: '/quote', anchor: 'free roofing estimate' },
      { keyword: 'roofing contractor', url: '/about', anchor: 'experienced roofing contractors' },
      { keyword: 'emergency', url: '/services/emergency', anchor: 'emergency roofing services' },
      { keyword: 'insurance', url: '/resources/insurance-claims', anchor: 'insurance claim assistance' }
    ];

    linkOpportunities.forEach(opportunity => {
      const regex = new RegExp(opportunity.keyword, 'gi');
      const matches = content.match(regex);
      
      if (matches && matches.length > 0 && currentPage !== opportunity.url) {
        suggestions.push({
          anchor: opportunity.anchor,
          url: opportunity.url,
          relevance: matches.length * 10
        });
      }
    });

    return suggestions.sort((a, b) => b.relevance - a.relevance);
  }

  // Generate structured data for rich snippets
  generateFAQStructuredData(faqs: Array<{ question: string; answer: string }>): object {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  }

  generateServiceStructuredData(serviceName: string, description: string, location: string): object {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": serviceName,
      "description": description,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Carolina Trophy Roofs",
        "url": "https://carolinatrophyroofs.com",
        "telephone": "(252) 302-2003",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": location,
          "addressRegion": "NC",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "State",
        "name": "Eastern North Carolina"
      },
      "url": `${this.baseUrl}/services/${serviceName.toLowerCase().replace(/\s+/g, '-')}`
    };
  }

  // Performance optimization helpers
  generateCriticalCSS(): string {
    return `
      /* Critical CSS for above-the-fold content */
      .hero-section { display: block; }
      .navigation { display: flex; }
      .logo { display: inline-block; }
      .cta-button { 
        background-color: #ff6b35; 
        color: white; 
        padding: 12px 24px; 
        border-radius: 6px; 
        text-decoration: none;
        font-weight: 600;
      }
    `;
  }

  generatePreloadTags(): Array<{ rel: string; href: string; as?: string; type?: string }> {
    return [
      { rel: 'preload', href: '/fonts/primary-font.woff2', as: 'font', type: 'font/woff2' },
      { rel: 'preload', href: '/images/logo.png', as: 'image' },
      { rel: 'prefetch', href: '/contact', as: 'document' },
      { rel: 'prefetch', href: '/quote', as: 'document' }
    ];
  }
}

export const technicalSEOManager = new TechnicalSEOManager();